import { SxProps, Theme } from "@mui/material/styles";
import { MouseEvent, useCallback, useMemo, useState } from "react";
import Lightbox from "react-awesome-lightbox";

type FullscreenImageItem = { id: string; name: string; image_url: string };

interface Props {
  images: FullscreenImageItem[];
}

export const useFullscreenImage = ({ images }: Props) => {
  const [imageOpened, setImageOpened] = useState(false);
  const [startIndex, setStartIndex] = useState(0);

  const openImage = useCallback(
    (event: MouseEvent<HTMLImageElement>, item?: FullscreenImageItem) => {
      event.stopPropagation();
      const index = item
        ? images.findIndex((image) => image.id === item.id)
        : -1;
      setStartIndex(index >= 0 ? index : 0);
      setImageOpened(true);
    },
    [images]
  );

  const closeImage = useCallback(() => setImageOpened(false), []);

  const lightboxProps = useMemo(
    () =>
      images.length === 1
        ? { image: images[0].image_url, title: images[0].name }
        : {
            images: images.map((image) => ({
              url: image.image_url,
              title: image.name,
            })),
            startIndex,
          },
    [images, startIndex]
  );

  const FullscreenImage = useMemo(
    () =>
      imageOpened ? (
        <Lightbox {...lightboxProps} allowRotate={false} onClose={closeImage} />
      ) : null,
    [closeImage, imageOpened, lightboxProps]
  );

  const imageStyles: SxProps<Theme> = useMemo(
    () => ({
      objectFit: "contain",
      "&:hover": {
        cursor: "zoom-in",
      },
    }),
    []
  );

  return {
    FullscreenImage,
    openImage,
    imageStyles,
  };
};
